import React from "react";
import {
  Grid,
  Card,
  CardContent,
  Avatar,
  Typography,
  Box,
  useTheme,
} from "@mui/material";
import {
  BusinessCenter,
  Person,
  Phone,
  LocationOn,
  AttachMoney,
} from "@mui/icons-material";

const CompanyProfileCard = ({ companyData }) => {
  const theme = useTheme();

  return (
    <Grid item xs={12} md={4}>
      <Card
        sx={{
          p: 0,
          mb: 3,
          backgroundColor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(0, 0, 0, 0.06)',
          transition: "all 0.3s ease-in-out",
          overflow: 'hidden',
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
          },
        }}
      >
        {/* Header Section with Gradient */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            p: 3,
            textAlign: 'center',
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #667eea, #764ba2)',
            }
          }}
        >
          <Avatar
            sx={{
              width: 80,
              height: 80,
              margin: "auto",
              mb: 2,
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              color: '#ffffff',
              border: '3px solid rgba(255, 255, 255, 0.3)',
            }}
          >
            <BusinessCenter style={{ fontSize: 40 }} />
          </Avatar>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 600, 
              color: '#ffffff',
              mb: 1,
              textShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            Company Profile
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 700, 
              color: '#ffffff',
              fontSize: '1.3rem',
              textShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            {companyData?.companyName || 'Company Name'}
          </Typography>
        </Box>

        {/* Content Section */}
        <CardContent sx={{ p: 3 }}>
          {companyData && (
            <Box>
              {/* Contact Person */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 2.5,
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    backgroundColor: '#667eea',
                    color: 'white',
                  }}
                >
                  <Person fontSize="small" />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Contact Person
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#1a202c' }}>
                    {companyData.contactPerson}
                  </Typography>
                </Box>
              </Box>

              {/* Phone Number */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 2.5,
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: '#f0fff4',
                  border: '1px solid #c6f6d5',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    backgroundColor: '#48bb78',
                    color: 'white',
                  }}
                >
                  <Phone fontSize="small" />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Phone Number
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#1a202c' }}>
                    {companyData.phoneNumber}
                  </Typography>
                </Box>
              </Box>

              {/* Address */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 2.5,
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: '#fefcbf',
                  border: '1px solid #faf089',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    backgroundColor: '#ed8936',
                    color: 'white',
                  }}
                >
                  <LocationOn fontSize="small" />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Address
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#1a202c' }}>
                    {companyData.address}
                  </Typography>
                </Box>
              </Box>

              {/* Balance */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: '#e6fffa',
                  border: '1px solid #81e6d9',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    backgroundColor: '#38b2ac',
                    color: 'white',
                  }}
                >
                  <AttachMoney fontSize="small" />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Current Balance
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 700, 
                      color: '#38b2ac',
                      fontSize: '1.25rem'
                    }}
                  >
                    ${companyData.balance.toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    </Grid>
  );
};

export default CompanyProfileCard;